/**
 * <Box> layout component that encapsulates CSS flexbox and basic appearance styling
 * rules.
 */
import * as React from "react"
import styled from "@oui/system"
import { AriaLabelledByProp } from "@oui/system/accessibility"
import * as Appearance from "@oui/system/appearance"
import * as Dimensions from "@oui/system/dimensions"
import * as Flexbox from "@oui/system/flexbox"
import { Role } from "@oui/system/roles"
import {
  propsToMediaQueryStyles,
  Responsive,
  ResponsiveProps
} from "@oui/system/breakpoints"
import { omit } from "ramda"

type DefaultProps = typeof defaultProps
type BaseProps = Pick<React.HTMLAttributes<HTMLDivElement>, "children" | "id">
type BreakpointAwareProps = ResponsiveProps<
  Appearance.Props & Dimensions.Props & Omit<Flexbox.Props, keyof DefaultProps>
>
export type Props = Readonly<
  {
    /**
     * ARIA defines "role" semantics which can be applied to elements, in the case
     * of a Box any valid ARIA role is allowed.
     */
    role?: Role
  } & BaseProps &
    AriaLabelledByProp &
    BreakpointAwareProps &
    DefaultProps
>

const defaultProps = {
  /**
   * The CSS flexbox flex-direction property.
   * @default "column"
   */
  direction: "column" as Responsive<Flexbox.Direction>,
  /**
   * The underlying DOM element.
   * @default "div"
   */
  from: "div" as keyof JSX.IntrinsicElements | React.ComponentType<any>
}

const { default: appearanceInterpolations } = Appearance
const { default: dimensionInterpolations } = Dimensions
const { default: flexBoxInterpolations } = Flexbox
const interpolations = {
  ...appearanceInterpolations,
  ...dimensionInterpolations,
  ...flexBoxInterpolations
}

const Box_ = ({ from: Component, ...rest }: Props) => (
  <Component {...omit(Object.keys(interpolations), rest)} />
)
Box_.defaultProps = defaultProps

const Box = styled(Box_)(
  {},
  propsToMediaQueryStyles(Object.values(interpolations))
)
Box.defaultProps = defaultProps

export { interpolations }
export default Box
